
import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import ConsentSection from './ConsentSection';
import Toast from './Toast';
import SurveyHeader from './SurveyHeader';
import ProgressBar from './ProgressBar';
import LoadingState from './LoadingState';
import ErrorState from './ErrorState';
import SuccessState from './SuccessState';
import styles from './Index.module.css';
import SavingIndicator from './SavingIndiactor';
import { useSurveyForm } from './Hooks/useSurveyForm';
import DynamicFormRenderer from './DynamicForm/DynamicForm';

const MainForms = ({ studyId: propStudyId, userId: propUserId }) => {

    const location = useLocation();

    const { subject, isEditMode = false, isViewOnly = false } = location.state || {};

    const getUserId = () => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            return user.userId || user.user_id || user.id || null;
        } catch (error) {
            return null;
        }
    };

    const getUserName = () => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            return user.fullName || 'Doctor';
        } catch (error) {
            return 'Doctor';
        }
    };

    const getUserRole = () => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            return user.role?.roleName || user.roleName || '';
        } catch (error) {
            return '';
        }
    };

    const userId = getUserId();
    const userRole = getUserRole();
    const isCRO = userRole === 'CRO Administrator' || userRole === 'CRO';

    const {
        studyData,
        currentSectionIndex,
        setCurrentSectionIndex,
        formData,
        updateFormData,
        isSubmitting,
        isSubmitted,
        isLoading,
        error,
        hasConsented,
        setHasConsented,
        isEligible,
        setIsEligible,
        isSavingDraft,
        autoSaveDraft,
        validateAllSections,
        handleSubmit: submitSurvey
    } = useSurveyForm(userId, subject.studyId, subject, isEditMode, isViewOnly, userRole);

    const [toast, setToast] = useState(null);

    const showToast = (title, description, variant = 'default') => {
        setToast({ title, description, variant });
        setTimeout(() => setToast(null), 5000);
    };

    const handleEligibilityConfirm = async (eligible) => {
        setIsEligible(eligible);

        if (!eligible) {
            const lastSectionIndex = studyData.sections.length - 1;
            setCurrentSectionIndex(lastSectionIndex);
            await autoSaveDraft(formData, lastSectionIndex, hasConsented, false);
            window.scrollTo(0, 0);
            showToast(
                'Subject Excluded',
                'Subject marked as excluded. Proceeding to final declaration.',
                'info'
            );
        }
    };

    const handleNavigateToLastSection = async () => {
        const lastSectionIndex = studyData.sections.length - 1;
        setCurrentSectionIndex(lastSectionIndex);
        await autoSaveDraft(formData, lastSectionIndex, hasConsented, false);
        window.scrollTo(0, 0);
        showToast(
            'Subject Excluded',
            'Subject marked as excluded. Proceeding to final declaration.',
            'info'
        );
    };

    // Helper function to get all fields from a section (handles both old and new structure)
    const getAllFieldsFromSection = (section) => {
        const allFields = [];

        // New structure: subsections with fields
        if (section.subsections && Array.isArray(section.subsections)) {
            section.subsections.forEach(subsection => {
                if (subsection.fields && Array.isArray(subsection.fields)) {
                    allFields.push(...subsection.fields);
                }
            });
        }
        // Old structure: direct questions
        else if (section.questions && Array.isArray(section.questions)) {
            // Flatten nested questions (like groups)
            const flattenQuestions = (questions) => {
                questions.forEach(q => {
                    if (q.type === 'group' && q.questions) {
                        flattenQuestions(q.questions);
                    } else {
                        allFields.push(q);
                    }
                });
            };
            flattenQuestions(section.questions);
        }

        return allFields;
    };

    // Auto-fill doctor name in last section
    useEffect(() => {
        if (!studyData || isSubmitted || isLoading) return;

        const isLastSection = currentSectionIndex === studyData.sections.length - 1;

        if (isLastSection) {
            const lastSection = studyData.sections[currentSectionIndex];

            // Get all fields from the section (handles both structures)
            const allFields = getAllFieldsFromSection(lastSection);

            // Find doctor/investigator field
            const doctorField = allFields.find(field => {
                const id = (field.id || '').toLowerCase();
                const label = (field.label || field.text || '').toLowerCase();

                return id.includes('doctor') ||
                    id.includes('physician') ||
                    id.includes('investigator') ||
                    label.includes('doctor name') ||
                    label.includes('physician name') ||
                    label.includes('investigator name');
            });

            if (doctorField) {
                const isAlreadyFilled = formData[doctorField.id] && formData[doctorField.id].trim() !== '';

                if (!isAlreadyFilled) {
                    const userName = getUserName();
                    updateFormData({
                        [doctorField.id]: userName
                    });

                    showToast(
                        'Field Auto-Filled',
                        'Doctor name has been filled with your account name',
                        'success'
                    );
                }
            }
        }
    }, [currentSectionIndex, studyData, isSubmitted, isLoading]);

    const navigateToSection = async (sectionIndex) => {
        // Allow navigation to any section if excluded
        if (isEligible === false) {
            // For excluded subjects, only allow navigation to first section (eligibility) and last section (declaration)
            const lastSectionIndex = studyData.sections.length - 1;
            if (sectionIndex !== 0 && sectionIndex !== lastSectionIndex) {
                showToast('Section Not Available', 'This section is not available for excluded subjects.', 'error');
                return;
            }
        } else {
            // For eligible subjects, prevent skipping ahead
            if (sectionIndex > currentSectionIndex + 1) {
                showToast('Cannot Skip Sections', 'Please complete the current section first before jumping ahead.', 'error');
                return;
            }
        }

        if (sectionIndex !== currentSectionIndex && sectionIndex >= 0 && sectionIndex < studyData.sections.length) {
            setCurrentSectionIndex(sectionIndex);
            await autoSaveDraft(formData, sectionIndex, hasConsented, isEligible);
            window.scrollTo(0, 0);
        }
    };

    const handleNext = async () => {
        if (currentSectionIndex < studyData.sections.length - 1) {
            const nextIndex = currentSectionIndex + 1;
            setCurrentSectionIndex(nextIndex);
            await autoSaveDraft(formData, nextIndex, hasConsented, isEligible);
            window.scrollTo(0, 0);
        } else {
            // On last section, validate and submit
            // UPDATED: Only validate if subject is eligible
            if (isEligible !== false) {
                const incompleteSections = validateAllSections();

                if (incompleteSections.length > 0) {
                    const sectionNumbers = incompleteSections.map(i => i + 1).join(', ');
                    showToast(
                        'Incomplete Sections',
                        `Please complete all required fields in section(s): ${sectionNumbers}`,
                        'error'
                    );
                    setCurrentSectionIndex(incompleteSections[0]);
                    window.scrollTo(0, 0);
                    return;
                }
            }

            const result = await submitSurvey();
            if (result.success) {
                showToast('Success!', 'Your survey has been submitted successfully.', 'success');
            } else {
                if (result.error && result.error.includes('already been submitted')) {
                    showToast('Already Submitted', result.error, 'info');
                } else {
                    showToast('Error', result.error || 'Failed to submit survey. Please try again.', 'error');
                }
            }
        }
    };

    // const handleBack = async () => {
    //     if (currentSectionIndex > 0) {
    //         const prevIndex = currentSectionIndex - 1;
    //         setCurrentSectionIndex(prevIndex);
    //         await autoSaveDraft(formData, prevIndex, hasConsented, isEligible);
    //         window.scrollTo(0, 0);
    //     }
    // };
    const handleBack = async () => {
        if (currentSectionIndex > 0) {
            let prevIndex;

            // If user is ineligible and on last section, go back to first section (eligibility)
            if (isEligible === false && currentSectionIndex === studyData.sections.length - 1) {
                prevIndex = 0; // Go to eligibility section
            } else {
                prevIndex = currentSectionIndex - 1;
            }

            setCurrentSectionIndex(prevIndex);
            await autoSaveDraft(formData, prevIndex, hasConsented, isEligible);
            window.scrollTo(0, 0);
        }
    };

    const handleConsentAccept = async () => {
        setHasConsented(true);
        const updatedData = { ...formData, consent_agreement: true };
        updateFormData({ consent_agreement: true });
        await autoSaveDraft(updatedData, 0, true, isEligible);
    };

    if (!subject.studyId) {
        return (
            <div className={styles.pageContainer}>
                <div className={styles.errorContainer}>
                    <div className={styles.errorCard}>
                        <svg className={styles.errorIcon} viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <h2 className={styles.errorTitle}>Invalid Survey Link</h2>
                        <p className={styles.errorText}>The survey link is missing required parameters.</p>
                    </div>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return <LoadingState />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={() => window.location.reload()} />;
    }

    if (isSubmitted) {
        return <SuccessState />;
    }

    const currentSection = studyData?.sections[currentSectionIndex];
    const isLastSection = currentSectionIndex === studyData?.sections.length - 1;

    return (
        <div className={styles.pageContainer}>
            <SavingIndicator isSaving={isSavingDraft} />
            <Toast toast={toast} onClose={() => setToast(null)} />
            <SurveyHeader studyData={studyData} />
            <ProgressBar
                studyData={studyData}
                currentSectionIndex={currentSectionIndex}
                onNavigateToSection={navigateToSection}
                formData={formData}
                isEligible={isEligible}
            />

            <main className={styles.mainContent}>
                {currentSection && (
                    <>
                        {console.log('=== RENDERING FORM ===', 'formData keys:', Object.keys(formData), 'formData:', formData)}
                        <DynamicFormRenderer
                            section={currentSection}
                            data={formData}
                            onUpdate={updateFormData}
                        onNext={handleNext}
                        onBack={currentSectionIndex > 0 ? handleBack : null}
                        isLastSection={isLastSection}
                        isSubmitting={isSubmitting}
                        isSubmitted={isSubmitted}
                        onEligibilityConfirm={handleEligibilityConfirm}
                        onNavigateToLastSection={handleNavigateToLastSection}
                        isEligible={isEligible}
                        allSections={studyData?.sections}
                        currentSectionIndex={currentSectionIndex}
                        onNavigateToSection={navigateToSection}
                        isViewOnly={isViewOnly}
                        isEditMode={isEditMode}
                        userRole={userRole}
                        isCRO={isCRO}
                    />
                    </>
                )}
            </main>
        </div>
    );
};

export default MainForms;